import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useBlocker,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { deepEqual } from "fast-equals";
import { SaveIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Editor } from "tldraw";

import { ErrorBanner } from "@/components/Callout";
import {
  customSortSettingsOf,
  gymSettingWrites,
  lensListHasGym,
} from "@/components/canvas-editor/gym-settings";
import { DepreactedFeedFeaturesBanner } from "@/components/DepreactedFeedFeaturesBanner";
import { FeedAvatarInput } from "@/components/FeedAvatarInput";
import { FeedDescriptionInput } from "@/components/FeedDescriptionInput";
import { FeedEditor, FeedEditorHandle } from "@/components/FeedEditor";
import { FeedForm } from "@/components/FeedForm";
import { FeedManifestDeliverySettings } from "@/components/FeedManifestDeliverySettings";
import { FeedSlugInput } from "@/components/FeedSlugInput";
import { FeedTitleInput } from "@/components/FeedTitleInput";
import { NodeAttentionBanner } from "@/components/NodeAttentionBanner";
import { HideSensitiveContentContext } from "@/components/PreviewFeed";
import { PublishBadge } from "@/components/PublishBadge";
import { SimpleEditor } from "@/components/SimpleEditor";
import { Button, ButtonLink } from "@/components/ui/button";
import { H2 } from "@/components/ui/typography";
import {
  feedQueryOptions,
  getCurrentUserQueryOptions,
  getUserCustomNodesQueryOptions,
  publishFeed,
  searchCustomNodesQueryOptions,
  setAlgoSetting,
} from "@/lib/api";
import { saveFeedLogic } from "@/lib/feedWrites";
import { FeedVersion, isSimpleEditorSnapshot } from "@/lib/types";
import { useFeedAccess } from "@/lib/useFeedAccess";

export const Route = createFileRoute("/app/_sidebar-layout/feed/$feedId/edit")({
  component: RouteComponent,
  validateSearch: (search: {
    fullscreen?: boolean;
    customNode?: string;
  }): { fullscreen?: boolean; customNode?: number } => {
    // validate and parse the search params into a typed state
    return {
      fullscreen: search?.fullscreen === true || undefined,
      customNode: search?.customNode ? parseInt(search.customNode) : undefined,
    };
  },
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    void queryClient.ensureQueryData(getUserCustomNodesQueryOptions());
    void queryClient.prefetchInfiniteQuery(
      searchCustomNodesQueryOptions({ query: "", favorited_only: true }),
    );

    const [feed] = await Promise.all([
      queryClient.ensureQueryData(feedQueryOptions(parseInt(feedId))),
      queryClient.ensureQueryData(getCurrentUserQueryOptions()),
    ]);

    return {
      title: feed.display_name,
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.title }],
  }),
});

function RouteComponent() {
  const { feedId } = Route.useParams();
  const { data: feed } = useSuspenseQuery(feedQueryOptions(parseInt(feedId)));
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const navigate = useNavigate({ from: "/app/feed/$feedId/edit" });
  // `edit_feed_logic` goes through the collaborator door, which saves a draft and refuses
  // the identity fields the owner's save carries. See lib/feedWrites.ts.
  const editorAccess = useFeedAccess(feed);
  const { fullscreen, customNode } = Route.useSearch();
  const shouldBlock = useRef(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const onSubmit = useMutation({
    mutationFn: async (form: FormData) => {
      await saveFeedLogic({
        feedId: feed.id,
        form,
        asCollaborator: editorAccess.isCollaborator,
      });

      // The gym follows the saved page: a gym layer turns it on (and every
      // reader in), removing one turns it off. See gym-settings.ts.
      for (const write of gymSettingWrites(
        feed.settings,
        lensListHasGym(
          (
            feed.draft_algorithm_manifest ??
            (feed as { algorithm_manifest?: FeedVersion }).algorithm_manifest
          )?.custom_sort_settings,
        ),
        lensListHasGym(customSortSettingsOf(form)),
      )) {
        await setAlgoSetting(feed.id, write);
      }

      if (isPublishing) {
        await publishFeed(feed.id);
      }
    },
    onSuccess: async () => {
      shouldBlock.current = false;
      await navigate({
        to: "/app/feed/$feedId/view",
        params: { feedId: `${feed.id}` },
      });
    },
    onError: (error: AxiosError) => console.error(error),
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const formRef = useRef<HTMLFormElement>(null!);
  const inputRef = useRef<HTMLInputElement>(null!);
  const feedHandleRef = useRef<FeedEditorHandle>(null!);
  const editorRef = useRef<Editor>(null!);

  useBlocker({
    disabled: !hasUnsavedChanges,
    shouldBlockFn: (event) => {
      if (event.action === "REPLACE") {
        return false;
      }

      if (shouldBlock.current) {
        const shouldNav = confirm(
          "Are you sure you want to leave? This will discard all your changes.",
        );
        return !shouldNav;
      }

      return false;
    },
  });

  const loadedFeedVersion =
    feed.draft_algorithm_manifest ||
    (feed as { algorithm_manifest?: FeedVersion }).algorithm_manifest;
  const [feedVersion, setFeedVersion] = useState<FeedVersion | undefined>(
    loadedFeedVersion
      ? {
          ...loadedFeedVersion,
          created_at: feed.created_at,
        }
      : undefined,
  );
  const setAlgoSettingMutation = useMutation({
    mutationFn: async (options: {
      setting_name: string;
      setting_value: string;
    }) => {
      await setAlgoSetting(feed.id, options);
    },
  });

  const setTestPosts = useCallback(
    (testPosts: string[]) => {
      setAlgoSettingMutation.mutate({
        setting_name: "test_posts",
        setting_value: JSON.stringify(testPosts),
      });
    },
    [setAlgoSettingMutation],
  );

  const isSimpleFeed =
    Boolean(loadedFeedVersion?.metadata?.snapshot) &&
    isSimpleEditorSnapshot(loadedFeedVersion?.metadata?.snapshot);
  const hideSensitiveContent = !(
    feed.settings?.show_nsfw ??
    user.settings?.show_nsfw ??
    false
  );

  return (
    <FeedForm
      formRef={formRef}
      onSubmit={onSubmit.mutate}
      feedVersion={feedVersion}
      feedEditorRef={feedHandleRef}
      editorRef={editorRef}
    >
      {onSubmit.error && <ErrorBanner error={onSubmit.error} />}
      <DepreactedFeedFeaturesBanner feedId={feedId} />
      <NodeAttentionBanner feedId={feedId} />
      <div className="flex flex-col gap-4 items-start">
        <PublishBadge feed={feed} />
        <div className="flex gap-4 items-center border-b-2 border-slate-7 dark:border-slatedark-7 pb-4 self-stretch">
          <FeedAvatarInput
            existingAvatarUrl={feed.avatar_url}
            feedName={feed.display_name}
          />
          <FeedTitleInput defaultValue={feed.display_name} ref={inputRef} />
        </div>
      </div>

      <FeedSlugInput defaultValue={feed.record_name} />
      <FeedDescriptionInput defaultValue={feed.description} />
      <FeedManifestDeliverySettings
        emit={feedVersion?.emit}
        onEmitNull={feedVersion?.onEmitNull}
        onEmitChange={(emit) => {
          setHasUnsavedChanges(true);
          setFeedVersion((currentFeedVersion) =>
            currentFeedVersion ? { ...currentFeedVersion, emit } : currentFeedVersion,
          );
        }}
        onEmitNullChange={(onEmitNull) => {
          setHasUnsavedChanges(true);
          setFeedVersion((currentFeedVersion) =>
            currentFeedVersion
              ? { ...currentFeedVersion, onEmitNull }
              : currentFeedVersion,
          );
        }}
      />
      <HideSensitiveContentContext value={hideSensitiveContent}>
        {isSimpleFeed ? (
          <>
            <H2 className="mt-8 mb-2">Feed Definition</H2>
            <SimpleEditor
              feedId={feed.id}
              feedVersion={feedVersion || loadedFeedVersion}
              onChange={(newVersion) => {
                setFeedVersion((currentFeedVersion) => ({
                  ...newVersion,
                  created_at: feed.created_at,
                  emit: newVersion.emit ?? currentFeedVersion?.emit,
                  onEmitNull:
                    newVersion.onEmitNull ?? currentFeedVersion?.onEmitNull,
                }));

                if (
                  deepEqual(
                    JSON.parse(
                      JSON.stringify({
                        manifest: newVersion.manifest,
                        custom_sort_settings:
                          newVersion.custom_sort_settings || {},
                        emit: newVersion.emit ?? feedVersion?.emit,
                        onEmitNull: newVersion.onEmitNull ?? feedVersion?.onEmitNull,
                      }),
                    ),
                    JSON.parse(
                      JSON.stringify({
                        manifest: feedVersion?.manifest,
                        custom_sort_settings:
                          feedVersion?.custom_sort_settings || {},
                        emit: feedVersion?.emit,
                        onEmitNull: feedVersion?.onEmitNull,
                      }),
                    ),
                  )
                ) {
                  return;
                }

                setHasUnsavedChanges(true);
              }}
            />
          </>
        ) : (
          <FeedEditor
            handle={feedHandleRef}
            editorRef={editorRef}
            defaultMode={feed.settings?.editor_mode}
            defaultCustomNodeId={customNode}
            feedId={feed.id}
            feedVersion={feedVersion}
            testPosts={
              feed.settings?.test_posts
                ? (JSON.parse(feed.settings.test_posts) as string[])
                : []
            }
            onDebugPost={setTestPosts}
            onChange={(newVersion) => {
              setFeedVersion((currentFeedVersion) => ({
                ...newVersion,
                created_at: feed.created_at,
                emit: newVersion.emit ?? currentFeedVersion?.emit,
                onEmitNull: newVersion.onEmitNull ?? currentFeedVersion?.onEmitNull,
              }));

              if (
                deepEqual(
                  JSON.parse(
                    JSON.stringify({
                      content_mode: newVersion.content_mode,
                      manifest: newVersion.manifest,
                      custom_sort_settings:
                        newVersion.custom_sort_settings || {},
                      emit: newVersion.emit ?? feedVersion?.emit,
                      onEmitNull: newVersion.onEmitNull ?? feedVersion?.onEmitNull,
                    }),
                  ),
                  JSON.parse(
                    JSON.stringify({
                      content_mode: feedVersion?.content_mode,
                      manifest: feedVersion?.manifest,
                      custom_sort_settings:
                        feedVersion?.custom_sort_settings || {},
                      emit: feedVersion?.emit,
                      onEmitNull: feedVersion?.onEmitNull,
                    }),
                  ),
                )
              ) {
                return;
              }

              setHasUnsavedChanges(true);
            }}
            centerContent={true}
            defaultFullscreen={fullscreen}
            fullscreenActions={
              <>
                <Button
                  size="xs"
                  variant="outline"
                  isLoading={onSubmit.isPending && !isPublishing}
                  disabled={onSubmit.isPending}
                  onClick={() => {
                    formRef.current.requestSubmit();
                  }}
                  className="hidden md:flex"
                >
                  Save Draft
                </Button>
                <Button
                  size="smIcon"
                  variant="outline"
                  isLoading={onSubmit.isPending && !isPublishing}
                  disabled={onSubmit.isPending}
                  onClick={() => formRef.current.requestSubmit()}
                  className="md:hidden"
                >
                  <SaveIcon />
                </Button>
                <Button
                  size="xs"
                  isLoading={onSubmit.isPending && isPublishing}
                  disabled={onSubmit.isPending}
                  onClick={() => {
                    setIsPublishing(true);
                    formRef.current.requestSubmit();
                  }}
                >
                  Publish
                </Button>
              </>
            }
          />
        )}
      </HideSensitiveContentContext>

      <div className="flex justify-between gap-4">
        <div className="flex-1" />
        <div className="flex gap-2">
          <ButtonLink
            size="lg"
            variant="ghost"
            to="/app/feed/$feedId/view"
            params={{ feedId: `${feed.id}` }}
          >
            Discard
          </ButtonLink>
          <Button
            size="lg"
            variant="outline"
            isLoading={onSubmit.isPending && !isPublishing}
            disabled={onSubmit.isPending}
            onClick={() => formRef.current.requestSubmit()}
          >
            Save
          </Button>
          <Button
            size="lg"
            isLoading={onSubmit.isPending && isPublishing}
            disabled={onSubmit.isPending}
            onClick={() => {
              setIsPublishing(true);
              formRef.current.requestSubmit();
            }}
          >
            Publish
          </Button>
        </div>
      </div>
    </FeedForm>
  );
}
